import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { ArrowLeft, Download, Send, Printer, Link2, Share2 } from "lucide-react";
import { api, errMsg, openBlob, API, setTenantCurrency } from "@/lib/api";
import { shareDocument } from "@/lib/share";
import { ReportView } from "@/components/ReportView";
import { Loading, ErrorState } from "@/components/common";
import { Button } from "@/components/ui/button";

export const useSendReport = (jobId) => async () => {
  try {
    const { data } = await api.post(`/jobs/${jobId}/report/send`);
    if (data.email.status === "failed") toast.warning(`Report was not delivered: ${data.email.error}. Share the report link instead.`, { duration: 8000 });
    else toast.success(`Completion report emailed to ${data.email.to}`);
  } catch (e) { toast.error(errMsg(e)); }
};

export default function JobReport() {
  const { id } = useParams();
  const { data, isLoading, error } = useQuery({ queryKey: ["job-report", id], queryFn: () => api.get(`/jobs/${id}/report`).then((r) => r.data), retry: false });
  const send = useSendReport(id);
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorState message="Report could not be loaded." />;
  const copy = () => { navigator.clipboard?.writeText(`${window.location.origin}/r/${data.job.public_token}`); toast.success("Report link copied"); };
  const share = () => shareDocument({ title: `Job ${data.job.number} completion report`, text: `${data.business?.business_name || ""} - Job #${data.job.number} is complete. Your before and after report is ready:`, url: `${window.location.origin}/r/${data.job.public_token}`, pdfPath: `/jobs/${id}/report/pdf`, filename: `Job-Report-${data.job.number}.pdf` });
  return (
    <div className="space-y-4" data-testid="job-report-page">
      <Link to={`/jobs/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 no-print"><ArrowLeft size={14} /> Job {data.job.number}</Link>
      <div className="card-flat p-4 flex flex-wrap items-center gap-2 no-print fade-up" data-testid="report-actions">
        <h1 className="text-2xl font-bold mr-auto">Completion Report</h1>
        <Button onClick={send} className="btn-orange h-10" data-testid="email-report-button"><Send size={16} /> Email Report</Button>
        <Button variant="outline" className="h-10" onClick={() => openBlob(`/jobs/${id}/report/pdf`, `Job-Report-${data.job.number}.pdf`)} data-testid="download-report-button"><Download size={16} /> Download PDF</Button>
        <Button variant="outline" className="h-10" onClick={share} data-testid="share-report-button"><Share2 size={16} /> Share</Button>
        <Button variant="outline" className="h-10" onClick={copy} data-testid="copy-report-link-button"><Link2 size={16} /> Link</Button>
        <Button variant="outline" className="h-10" onClick={() => window.print()} data-testid="print-report-button"><Printer size={16} /></Button>
      </div>
      <ReportView data={data} />
    </div>
  );
}

export function PublicJobReport() {
  const { token } = useParams();
  const { data, isLoading, error } = useQuery({ queryKey: ["public-report", token], queryFn: () => api.get(`/public/jobs/${token}/report`).then((r) => r.data), retry: false });
  useEffect(() => { if (data) setTenantCurrency(data.invoice?.currency || data.job?.currency || data.business?.currency); }, [data]);
  if (isLoading) return <Loading full />;
  if (error || !data) return <div className="min-h-screen grid place-items-center text-slate-500">Report not found.</div>;
  return (
    <div className="min-h-screen bg-slate-100 py-6 px-3 sm:px-6" data-testid="public-report-page">
      <div className="max-w-3xl mx-auto space-y-4">
        <ReportView data={data} publicToken={token} />
        <div className="flex justify-center gap-4 no-print">
          <button onClick={() => window.print()} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="public-print-report"><Printer size={14} /> Print</button>
          <a href={`${API}/public/jobs/${token}/report/pdf`} target="_blank" rel="noreferrer" className="text-sm text-slate-500 hover:text-slate-900" data-testid="public-report-pdf-link">Download PDF</a>
        </div>
      </div>
    </div>
  );
}
